import { useCallback, useEffect, useState } from "react";
import * as admin from "@/lib/adminApi";

const message = error =>
  error?.response?.data?.detail ||
  error?.message ||
  "Could not reach the server";

/**
 * Staff, role permissions and the menu catalogue, from the server.
 *
 * The Admin page rendered a hardcoded `staff` array and a permission matrix whose
 * checkboxes used `defaultChecked` with no handler — so it showed three fictional
 * employees and a matrix that could be clicked but never saved. Both had working
 * endpoints the whole time.
 */
export function useAdminData(token) {
  const [staff, setStaff] = useState([]);
  const [menu, setMenu] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [rolePerms, setRolePerms] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const ROLES = ["admin", "cashier", "captain"];

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [users, menuData] = await Promise.all([
        admin.listUsers(token),
        admin.listMenu(token),
      ]);
      setStaff(users);
      setMenu(menuData);
      setError(null);
    } catch (failure) {
      setError(message(failure));
    } finally {
      setLoading(false);
    }
    // The permission catalogue needs permissions.manage, which a cashier viewing this
    // page does not have. Fetch it separately so a 403 there does not blank the
    // staff list too.
    try {
      const [cat, ...perRole] = await Promise.all([
        admin.permissionCatalog(token),
        ...ROLES.map(role => admin.rolePermissions(role, token)),
      ]);
      setCatalog(cat);
      setRolePerms(Object.fromEntries(ROLES.map((role, i) => [role, perRole[i]])));
    } catch {
      setCatalog([]);
      setRolePerms({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const saveStaff = useCallback(
    async (userId, body) => {
      try {
        if (userId) {
          const updated = await admin.updateUser(userId, body, token);
          await load();
          return { ok: true, data: updated };
        } else {
          const created = await admin.createUser(body, token);
          await load();
          return { ok: true, data: created };
        }
      } catch (failure) {
        return { ok: false, error: message(failure) };
      }
    },
    [load, token]
  );

  const deactivateStaff = useCallback(
    async userId => {
      try {
        await admin.deleteUser(userId, token);
        await load();
        return { ok: true };
      } catch (failure) {
        return { ok: false, error: message(failure) };
      }
    },
    [load, token]
  );

  const removeStaff = useCallback(
    async userId => {
      try {
        await admin.removeUser(userId, token);
        await load();
        return { ok: true };
      } catch (failure) {
        return { ok: false, error: message(failure) };
      }
    },
    [load, token]
  );

  const saveMenuItem = useCallback(
    async (itemId, body) => {
      try {
        if (itemId) await admin.updateMenuItem(itemId, body, token);
        else await admin.createMenuItem(body, token);
        await load();
        return { ok: true };
      } catch (failure) {
        return { ok: false, error: message(failure) };
      }
    },
    [load, token]
  );

  const menuItems = menu.flatMap(category =>
    (category.items || []).map(item => ({ ...item, category_name: category.name }))
  );

  return {
    staff,
    menu,
    menuItems,
    catalog,
    rolePerms,
    roles: ROLES,
    error,
    loading,
    saveStaff,
    deactivateStaff,
    removeStaff,
    saveMenuItem,
    reload: load,
  };
}
